/**
 * AlertsBanner — compact persistent banner on the query page.
 * Shows outstanding checklist items + unread amendment alerts.
 * Links to Admin Portal for details. Dismissible per session.
 */
import { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { authFetch } from '../api';

const API_BASE = import.meta.env.VITE_API_URL || '';

const SESSION_DISMISS_KEY = 'ch-alerts-banner-dismissed';

export default function AlertsBanner() {
  const navigate = useNavigate();
  const [outstanding, setOutstanding] = useState<number | null>(null);
  const [amendments, setAmendments] = useState<number>(0);
  const [dismissed, setDismissed] = useState(() =>
    sessionStorage.getItem(SESSION_DISMISS_KEY) === 'true'
  );

  const load = useCallback(async () => {
    try {
      const [oRes, aRes] = await Promise.all([
        authFetch(`${API_BASE}/api/dashboard/outstanding-items?limit=1&offset=0`),
        authFetch(`${API_BASE}/api/admin/amendment-alerts?dismissed=0`),
      ]);
      if (oRes.ok) {
        const d = await oRes.json() as { total?: number; items?: unknown[] };
        setOutstanding(d.total ?? d.items?.length ?? 0);
      }
      if (aRes.ok) {
        const d = await aRes.json() as { unread?: number; items?: unknown[] };
        setAmendments(d.unread ?? d.items?.length ?? 0);
      }
    } catch { /* silent */ }
  }, []);

  useEffect(() => { void load(); }, [load]);

  const handleDismiss = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    sessionStorage.setItem(SESSION_DISMISS_KEY, 'true');
    setDismissed(true);
  }, []);

  // Don't show if dismissed or nothing to report
  if (dismissed) return null;
  if (outstanding === null) return null; // still loading
  if (outstanding === 0 && amendments === 0) return null;

  const parts: string[] = [];
  if (outstanding > 0) parts.push(`${outstanding} outstanding item${outstanding !== 1 ? 's' : ''}`);
  if (amendments > 0) parts.push(`${amendments} amendment alert${amendments !== 1 ? 's' : ''}`);

  return (
    <div
      className="alerts-banner"
      onClick={() => navigate('/portal')}
      role="button"
      tabIndex={0}
      onKeyDown={e => e.key === 'Enter' && navigate('/portal')}
    >
      <span className="alerts-banner__icon">🔴</span>
      <span className="alerts-banner__text">
        <strong>{parts.join(' · ')}</strong>
        <span className="alerts-banner__sub"> — click to review in Admin Portal</span>
      </span>
      <button
        className="alerts-banner__dismiss"
        onClick={handleDismiss}
        aria-label="Dismiss alerts banner"
        title="Dismiss"
      >
        ✕
      </button>
    </div>
  );
}
